import { useSyncExternalStore } from "react";

// Otwarte karty klientek. Lista przeżywa odświeżenie strony (localStorage), parametry
// widoku (`search`) tylko sesję - po odświeżeniu karta w tle startuje od domyślnej zakładki.

export const MAX_OPEN_TRAINEES = 10;

const STORAGE_KEY = "trainee-workspace-cards";

export interface TraineeCard {
  traineeId: string;
  search: string;
}

const readStoredCards = (): TraineeCard[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids)
      ? ids
          .filter((id): id is string => typeof id === "string")
          .slice(0, MAX_OPEN_TRAINEES)
          .map((traineeId) => ({ traineeId, search: "" }))
      : [];
  } catch {
    return [];
  }
};

let cards: TraineeCard[] = readStoredCards();
const listeners = new Set<() => void>();

const setCards = (next: TraineeCard[]) => {
  cards = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next.map((card) => card.traineeId)));
  } catch {
    // brak localStorage (tryb prywatny) - karty żyją do końca sesji
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const traineeWorkspace = {
  getCards: () => cards,

  /** Otwiera kartę (albo aktualizuje jej parametry). Nadmiarowe karty wypadają od najstarszej. */
  open: (traineeId: string, search: string) => {
    const existing = cards.find((card) => card.traineeId === traineeId);

    if (existing) {
      if (existing.search !== search) {
        setCards(cards.map((card) => (card.traineeId === traineeId ? { ...card, search } : card)));
      }
      return;
    }

    const next = [...cards, { traineeId, search }];
    setCards(next.length > MAX_OPEN_TRAINEES ? next.slice(next.length - MAX_OPEN_TRAINEES) : next);
  },

  setSearch: (traineeId: string, search: string) => {
    if (cards.some((card) => card.traineeId === traineeId && card.search !== search)) {
      setCards(cards.map((card) => (card.traineeId === traineeId ? { ...card, search } : card)));
    }
  },

  close: (traineeId: string) => {
    setCards(cards.filter((card) => card.traineeId !== traineeId));
  },
};

export const useTraineeWorkspaceCards = () => useSyncExternalStore(subscribe, traineeWorkspace.getCards);
